/* eslint-disable tsdoc/syntax */
/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable tsdoc/syntax */
import { ChangeEvent, FocusEvent, memo, useState } from "react";

import { TextFieldComponent } from "@zenith/components";
import { useUserDetailsContext } from "@zenith/context/global-context/hooks";
import {
  DEFAULT_COUNTRY_CODE,
  FLAG_ICON,
  PHONE_NUMBER_PREFIX,
} from "@zenith/data/constants";
import useAppNavigation from "@zenith/hooks/appNavigation";
import { SETTINGS } from "@zenith/routes/paths";
import { getPhoneNumberLength } from "@zenith/features/globalUtils";
import { UserDetailsManagementRequestDTO } from "@zenith/services/user";
import {
  EmployeeProfileForm,
  EmployeeProfileFormValidation,
  ErrorField,
  EmployeeProfileFields,
} from "../../utils/form-fields";
import {
  NO_ERROR,
  preventSpace,
  processValue,
  validateEmployeeEmail,
  validatePhoneNumber,
  isEmployeeOTPRequired,
  employeeFormHasError,
  hasEmployeeFormChanged,
} from "../../validations/form-field-validations";

/* eslint-disable-next-line */
export interface EmployeeProfileComponentProps {
  updateProfile: (body: UserDetailsManagementRequestDTO) => void;
  updateProfileWithOTP: (body: UserDetailsManagementRequestDTO) => void;
  userFilledData: UserDetailsManagementRequestDTO | null;
}
const EmployeeProfileComponent = memo(function EmployeeProfileComponent({
  updateProfile,
  updateProfileWithOTP,
  userFilledData,
}: EmployeeProfileComponentProps) {
  const { userDetailsContext } = useUserDetailsContext();

  const navigation = useAppNavigation();
  const initialFormData = {
    firstName: userDetailsContext.firstName,
    lastName: userDetailsContext.lastName,
    phoneNumber: userDetailsContext.phoneNumber?.substring(4) ?? "",
    phoneNumberPrefix: userDetailsContext.phoneNumber?.substring(0, 4) ?? "",
    email: userDetailsContext.email,
  };

  const [formData, setFormData] = useState<EmployeeProfileForm>({
    ...initialFormData,
    ...(userFilledData && {
      ...userFilledData,
      phoneNumber: userFilledData.phoneNumber?.substring(4) ?? "",
    }),
  });

  const [formValidations, setFormValidations] =
    useState<EmployeeProfileFormValidation>({
      [EmployeeProfileFields.EMAIL]: formData[EmployeeProfileFields.EMAIL]
        ? validateEmployeeEmail(formData[EmployeeProfileFields.EMAIL])
        : NO_ERROR,
      [EmployeeProfileFields.PHONE_NUMBER]: validatePhoneNumber(
        formData[EmployeeProfileFields.PHONE_NUMBER]
      ),
    });

  const isDisabled = true;

  const onFieldChange = (field: EmployeeProfileFields) => {
    return (event: ChangeEvent<HTMLInputElement>) => {
      const newVal = event.target.value;
      setFormData({
        ...formData,
        [field]: processValue(field, newVal),
      });
      setFormValidations({
        ...formValidations,
        [field]: NO_ERROR,
      });
    };
  };

  const onFieldBlur = (
    field: keyof EmployeeProfileForm,
    validate: (value: string) => ErrorField
  ) => {
    return (_: FocusEvent<HTMLInputElement>) => {
      const value = formData[field];
      return setFormValidations({
        ...formValidations,
        [field]: validate(value),
      });
    };
  };

  const backToSettings = () => {
    navigation.navigateTo(SETTINGS);
  };

  const handleFormSubmit = () => {
    const payload = buildRequestBody();
    if (
      employeeFormHasError(formValidations) ||
      !hasEmployeeFormChanged(formData, initialFormData)
    ) {
      return;
    }

    if (payload) {
      if (isEmployeeOTPRequired(formData, initialFormData)) {
        updateProfileWithOTP(payload);
        return;
      }
      updateProfile(payload);
    }
  };

  const buildRequestBody = (): UserDetailsManagementRequestDTO | null => {
    const email = formData[EmployeeProfileFields.EMAIL];
    const phoneNumber =
      formData[EmployeeProfileFields.PHONE_NUMBER_PREFIX] +
      formData[EmployeeProfileFields.PHONE_NUMBER];
    if (phoneNumber) {
      return {
        ...(email ? { email } : {}),
        phoneNumber,
      };
    } else {
      return null;
    }
  };

  return (
    <form>
      <div className="w-full gap-4 sm:flex">
        <div className="grow">
          <TextFieldComponent
            id="first-name"
            label="First Name"
            labelId="label-first-name"
            type="text"
            placeholder=""
            onChange={() => void 0}
            disabled={isDisabled}
            value={formData[EmployeeProfileFields.FIRST_NAME]}
          />
        </div>
        <div className="mt-4 grow sm:mt-0">
          <TextFieldComponent
            id="last-name"
            label="Last Name"
            labelId="label-last-name"
            type="text"
            placeholder=""
            onChange={() => void 0}
            disabled={isDisabled}
            value={formData[EmployeeProfileFields.LAST_NAME]}
          />
        </div>
      </div>
      <div className="mt-4 flex w-full gap-4">
        <div>
          <label id="label-phone-prefix" className="label block">
            Phone Number
          </label>
          <div
            id="phone-prefix"
            className="text-field select-none bg-neutral-25"
          >
            <div className="align-center flex justify-center p-3 text-neutral-400">
              <FLAG_ICON />{" "}
              {formData[EmployeeProfileFields.PHONE_NUMBER_PREFIX] ||
                PHONE_NUMBER_PREFIX}
            </div>
          </div>
        </div>
        <div className="grow pt-6">
          <TextFieldComponent
            id="phone-number"
            label=""
            labelId="label-phone-number"
            type="tel"
            maxLength={getPhoneNumberLength(
              DEFAULT_COUNTRY_CODE,
              formData[EmployeeProfileFields.PHONE_NUMBER]
            )}
            placeholder="Enter phone number"
            onChange={onFieldChange(EmployeeProfileFields.PHONE_NUMBER)}
            value={formData[EmployeeProfileFields.PHONE_NUMBER]}
            onBlur={onFieldBlur(
              EmployeeProfileFields.PHONE_NUMBER,
              validatePhoneNumber
            )}
            disabled={isDisabled}
            hasError={
              formValidations[EmployeeProfileFields.PHONE_NUMBER]?.hasError
            }
          />
          {formValidations[EmployeeProfileFields.PHONE_NUMBER]?.hasError && (
            <div className="mt-1 ml-2 text-xs text-red-400">
              <span className="break-words">
                {formValidations[EmployeeProfileFields.PHONE_NUMBER].message}
              </span>
            </div>
          )}
        </div>
      </div>
      <div className="mt-4">
        <TextFieldComponent
          id="email-address"
          label="Email Address (optional)"
          labelId="label-email-address"
          type="email"
          placeholder="Enter email address"
          onChange={onFieldChange(EmployeeProfileFields.EMAIL)}
          onKeyDown={preventSpace}
          value={formData[EmployeeProfileFields.EMAIL]}
          onBlur={onFieldBlur(
            EmployeeProfileFields.EMAIL,
            validateEmployeeEmail
          )}
          disabled={isDisabled}
          hasError={formValidations[EmployeeProfileFields.EMAIL]?.hasError}
        />
      </div>
      {formValidations[EmployeeProfileFields.EMAIL]?.hasError && (
        <div className="mt-1 ml-2 text-xs text-red-400">
          <span className="break-words">
            {formValidations[EmployeeProfileFields.EMAIL].message}
          </span>
        </div>
      )}

      {/* <div className="mt-20 mb-12 flex">
        <div className="mr-3 w-full">
          <MajorButtonComponent
            id="cancel-button"
            className="w-full"
            isLoading={false}
            type="button"
            disabled={false}
            onClick={backToSettings}
            buttonType="secondary"
            size="large"
          >
            Cancel
          </MajorButtonComponent>
        </div>
        <div className="ml-3 w-full">
          <MajorButtonComponent
            id="save-changes-button"
            className="w-full"
            isLoading={false}
            type="button"
            disabled={
              employeeFormHasError(formValidations) ||
              !hasEmployeeFormChanged(formData, initialFormData)
            }
            buttonType="primary"
            size="large"
            onClick={handleFormSubmit}
          >
            Save Changes
          </MajorButtonComponent>
        </div> */}
      {/* </div> */}
    </form>
  );
});

export default EmployeeProfileComponent;
